import { getChatAPI } from '@/api/ChatApi';
import { getPlatformLabel } from '@/utils/variantBasedLabels';
import { mkUserQueryConsentKey } from '@/constants/localStorage';
import { optAppConfig } from '@/utils/config';
import { User } from '@/models/User';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import styled from 'styled-components';

type ConsentModalProps = {
  currentUser: User;
  onConsent: (errorSavingConsent: { errorSavingConsent: boolean }) => void;
};

type LoadingState = {
  isLoading: boolean;
  consentValue: boolean | null;
};

export const ConsentModal = ({ onConsent, currentUser }: ConsentModalProps) => {
  const chatApi = getChatAPI();
  const [loadingState, setLoadingState] = useState<LoadingState>({
    isLoading: false,
    consentValue: null,
  });

  const noraExperienceVariant = optAppConfig((_) => _.experience.variant);

  const setQueryConsentFlag = (): void => {
    window.localStorage.setItem(
      mkUserQueryConsentKey(currentUser.uuid),
      'true',
    );
  };

  const unsetQueryConsentFlag = (): void => {
    window.localStorage.removeItem(mkUserQueryConsentKey(currentUser.uuid));
  };

  const onClickConsent = async (consent: boolean): Promise<void> => {
    try {
      setLoadingState({ isLoading: true, consentValue: consent });
      await chatApi.saveDataContributionConsent(consent);
      setQueryConsentFlag();
      onConsent({ errorSavingConsent: false });
      setLoadingState({ isLoading: false, consentValue: null });
    } catch (error) {
      unsetQueryConsentFlag();
      onConsent({ errorSavingConsent: true });
      setLoadingState({ isLoading: false, consentValue: null });
    }
  };

  return (
    <Dialog
      open
      maxWidth="sm"
      sx={{
        '& .MuiDialog-paper': {
          maxHeight: 435,
        },
      }}
    >
      <Title>Terms of Use and Data Consent</Title>
      <Content>
        <Typography sx={{ marginBottom: '8px' }}>
          By using {getPlatformLabel(noraExperienceVariant)}, you agree to
          the&nbsp;
          <Link
            href="https://allenai.org/terms"
            target="_blank"
            sx={{ fontWeight: 'bold' }}
          >
            Terms of Use
          </Link>
          ,&nbsp;
          <Link
            href="https://allenai.org/responsible-use"
            target="_blank"
            sx={{ fontWeight: 'bold' }}
          >
            Responsible Use
          </Link>
          ,&nbsp;
          <Link
            href="https://allenai.org/privacy-policy"
            target="_blank"
            sx={{ fontWeight: 'bold' }}
          >
            Privacy Policy
          </Link>
          , and that you will not submit any sensitive or confidential info.
        </Typography>
        <Typography>
          Ai2 may use your anonymized queries in a public dataset for future AI
          research and development. You can still use this tool if you opt-out.
        </Typography>
      </Content>
      <Actions>
        <DisagreeButton
          loading={
            loadingState.isLoading && loadingState.consentValue === false
          }
          variant="text"
          size="medium"
          onClick={() => onClickConsent(false)}
        >
          I agree, but opt-out of query collection
        </DisagreeButton>
        <AgreeButton
          loading={loadingState.isLoading && loadingState.consentValue === true}
          variant="contained"
          onClick={() => onClickConsent(true)}
          color="secondary"
        >
          I agree, and ok to use my queries
        </AgreeButton>
      </Actions>
    </Dialog>
  );
};

const Title = styled(DialogTitle)`
  align-items: center;
  padding-bottom: ${({ theme }) => theme.spacing(2)};
  &.MuiDialogTitle-root {
    font-weight: 700;
    font-size: 18px;
  }
`;

const Content = styled(DialogContent)`
  border-bottom: 1px solid rgba(10, 50, 55, 0.2);
  padding: ${({ theme }) => theme.spacing(3)};
`;

const Actions = styled(DialogActions)`
  &.MuiDialogActions-root {
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing(4)};
    justify-content: space-around;
    padding: ${({ theme }) => theme.spacing(2)};

    ${({ theme }) => theme.breakpoints.down('sm')} {
      flex-direction: column-reverse;
      gap: ${({ theme }) => theme.spacing(1.75)};
    }
  }
`;

const AgreeButton = styled(Button)`
  margin: 0 ${({ theme }) => theme.spacing(4)};
`;

const DisagreeButton = styled(Button)`
  &.MuiButtonBase-root {
    color: ${({ theme }) => theme.color['dark-teal-100'].hex};
    background: transparent;
  }
`;
